import { doFetch, resolveBackendBase } from "./ConnectionManager";
import Constantes from "./Constantes";

function buildMfmEndpoint(method) {
  return resolveBackendBase()
    + "/"
    + Constantes.API_BASE
    + "/"
    + Constantes.URL_MFM
    + method;
}

function normalizeUploadError(status, text) {
  const payload = String(text || "").trim();
  if (!payload) {
    return `Error al subir fichero (${status || "sin código"})`;
  }

  if (payload.startsWith("<") && payload.toLowerCase().includes("<html")) {
    if (status === 500) return "Error interno del servidor durante la subida (500).";
    return `Error HTTP ${status || "desconocido"} durante la subida.`;
  }

  try {
    const json = JSON.parse(payload);
    if (json?.MESSAGE) return String(json.MESSAGE);
  } catch (_) {
    // Respuesta no JSON, usar texto en limpio.
  }

  return payload;
}

/**
 * Sube un fichero al MFM (imagen o vídeo).
 *
 * @param {File}   file         - Fichero del input o drag&drop
 * @param {Object} options
 * @param {string} options.appCode    - Código de la app propietaria ("TEAN", "CCON"…)
 * @param {string} [options.library]  - Slug de la biblioteca (default: "_default")
 * @param {string} [options.title]
 * @param {string} [options.altText]
 * @param {string} [options.tags]     - Coma-separated
 * @param {string} [options.entityType]
 * @param {number} [options.entityId]
 * @returns {Promise<Object>} MfmUploadResultDTO  { uuid, url, thumbUrl, mediumUrl, ... }
 */
export async function uploadFile(file, options = {}) {
  const formData = new FormData();
  formData.append("file", file);
  if (options.appCode) formData.append("app_code", options.appCode);
  if (options.library) formData.append("library_slug", options.library);
  if (options.title) formData.append("title", options.title);
  if (options.altText) formData.append("alt_text", options.altText);
  if (options.tags) formData.append("tags", options.tags);
  if (options.entityType) formData.append("entity_type", options.entityType);
  if (options.entityId != null) formData.append("entity_id", String(options.entityId));
  if (options.visibility) formData.append("visibility", options.visibility);
  if (options.contentHash) formData.append("content_hash", options.contentHash);

  const onProgress = typeof options.onProgress === "function" ? options.onProgress : null;
  const isMfmUpload = String(options.appCode || "").toUpperCase() === "MFM";
  const uploadEndpoint = isMfmUpload ? "upload-mfm" : "upload";

  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("POST", buildMfmEndpoint(uploadEndpoint), true);
    xhr.withCredentials = true;

    if (xhr.upload && onProgress) {
      xhr.upload.onprogress = (evt) => {
        if (!evt || !evt.lengthComputable) return;
        const pct = Math.max(0, Math.min(100, Math.round((evt.loaded / evt.total) * 100)));
        onProgress(pct, evt.loaded, evt.total);
      };
    }

    xhr.onerror = () => {
      reject(new Error("No se pudo conectar con el servidor durante la subida."));
    };

    xhr.onload = () => {
      const status = xhr.status;
      const raw = xhr.responseText || "";

      if (status >= 200 && status < 300) {
        try {
          const json = JSON.parse(raw || "{}");
          resolve(json?.DATA ?? null);
          return;
        } catch (_) {
          reject(new Error("Respuesta inválida del servidor tras la subida."));
          return;
        }
      }

      reject(new Error(normalizeUploadError(status, raw)));
    };

    xhr.send(formData);
  });
}

/**
 * Construye la URL pública de un fichero MFM.
 *
 * @param {string} uuid
 * @param {string} [size] - "thumb", "medium", "poster" — o vacío para original
 */
export function mfmUrl(uuid, size) {
  if (!uuid) return null;
  const base = resolveBackendBase();
  let url = base + "/api/media/" + uuid;
  if (size) url += "?size=" + size;
  return url;
}

export function mfmAppUrl(appCode, uuid, token, options = {}) {
  if (!appCode || !uuid || !token) return null;
  const base = resolveBackendBase();
  const params = new URLSearchParams();
  params.set("token", token);

  if (options.size) params.set("size", String(options.size));
  if (options.variant) params.set("variant", String(options.variant));
  if (options.w != null) params.set("w", String(options.w));
  if (options.h != null) params.set("h", String(options.h));
  if (options.format) params.set("format", String(options.format));
  if (options.x != null) params.set("x", String(options.x));
  if (options.y != null) params.set("y", String(options.y));
  if (options.zoom != null) params.set("zoom", String(options.zoom));

  return base + "/api/media/app/" + encodeURIComponent(appCode) + "/" + encodeURIComponent(uuid) + "?" + params.toString();
}

export function mfmPublicUrl(uuid, token, options = {}) {
  if (!uuid || !token) return null;
  const base = resolveBackendBase();
  const params = new URLSearchParams();
  params.set("token", token);

  if (options.size) params.set("size", String(options.size));
  if (options.variant) params.set("variant", String(options.variant));
  if (options.w != null) params.set("w", String(options.w));
  if (options.h != null) params.set("h", String(options.h));
  if (options.format) params.set("format", String(options.format));
  if (options.x != null) params.set("x", String(options.x));
  if (options.y != null) params.set("y", String(options.y));
  if (options.zoom != null) params.set("zoom", String(options.zoom));

  return base + "/api/media/public/" + encodeURIComponent(uuid) + "?" + params.toString();
}

/**
 * Atajos de URL para tamaños habituales.
 */
export const mfmThumbUrl    = (uuid) => mfmUrl(uuid, "thumb");
export const mfmMediumUrl   = (uuid) => mfmUrl(uuid, "medium");
export const mfmPosterUrl   = (uuid) => mfmUrl(uuid, "poster");
export const mfmOriginalUrl = (uuid) => mfmUrl(uuid);

/**
 * URL de streaming de un fichero de audio MFM.
 * Devuelve el original sin parámetro de tamaño; el backend lo sirve
 * con Content-Type audio/* para que AudioContext.decodeAudioData() funcione.
 * @param {string} uuid
 */
export const getAudioUrl = (uuid) => mfmUrl(uuid);

/**
 * URL de imagen con encuadre/medida fija servida por backend.
 * El backend genera (y cachea) una variante por uuid+format+w+h.
 */
export function mfmFramedUrl(uuid, width, height, formatKey, crop) {
  if (!uuid) return null;
  const w = Number(width);
  const h = Number(height);
  if (!Number.isFinite(w) || !Number.isFinite(h) || w <= 0 || h <= 0) {
    return mfmOriginalUrl(uuid);
  }

  const base = resolveBackendBase();

  const params = new URLSearchParams();
  params.set("w", String(Math.round(w)));
  params.set("h", String(Math.round(h)));
  if (formatKey) params.set("format", String(formatKey));

  const cx = Number(crop?.x);
  const cy = Number(crop?.y);
  const cz = Number(crop?.zoom);
  if (Number.isFinite(cx)) params.set("x", String(cx));
  if (Number.isFinite(cy)) params.set("y", String(cy));
  if (Number.isFinite(cz)) params.set("zoom", String(cz));

  return base + "/api/media/" + uuid + "?" + params.toString();
}

/**
 * URL de una variante persistida concreta (mfm_file_variants.variant_key).
 */
export function mfmVariantUrl(uuid, variantKey) {
  if (!uuid || !variantKey) return mfmOriginalUrl(uuid);
  const base = resolveBackendBase();

  const params = new URLSearchParams();
  params.set("variant", String(variantKey));
  return base + "/api/media/" + uuid + "?" + params.toString();
}

/**
 * Obtiene la lista de variantes persistidas de un fichero.
 */
export async function getFileVariants(uuid) {
  if (!uuid) return [];
  const response = await doFetch("MFM", uuid + "/variants", { method: "GET" });
  if (!response || !response.ok) return [];
  const json = await response.json().catch(() => ({}));
  return Array.isArray(json?.DATA) ? json.DATA : [];
}

/**
 * Elimina una variante persistida concreta por variantKey.
 */
export async function deleteFileVariant(uuid, variantKey) {
  if (!uuid || !variantKey) throw new Error("Faltan datos para eliminar variante");
  const qs = new URLSearchParams({ variant: String(variantKey) }).toString();
  const response = await doFetch("MFM", uuid + "/variants?" + qs, { method: "DELETE" });
  if (!response || !response.ok) throw new Error("Error al eliminar variante");
  const json = await response.json().catch(() => ({}));
  return json?.DATA || null;
}

/**
 * Elimina todas las variantes custom persistidas de un fichero.
 */
export async function deleteCustomVariants(uuid) {
  if (!uuid) throw new Error("Falta uuid para eliminar variantes");
  const response = await doFetch("MFM", uuid + "/variants/custom", { method: "DELETE" });
  if (!response || !response.ok) throw new Error("Error al eliminar variantes custom");
  const json = await response.json().catch(() => ({}));
  return json?.DATA || null;
}

/**
 * Obtiene el estado de transcodificación de un vídeo.
 */
export async function getTranscodeStatus(uuid) {
  const response = await doFetch("MFM", uuid + "/status", { method: "GET" });
  if (!response || !response.ok) return null;
  const json = await response.json();
  return json.DATA;
}

/**
 * Obtiene un resumen operativo del sistema MFM para dashboard.
 * @param {string} [appCode] filtro opcional por app
 */
export async function getMfmSystemStatus(appCode) {
  const qs = appCode ? "?app=" + encodeURIComponent(appCode) : "";
  const response = await doFetch("MFM", "system-status" + qs, { method: "GET" });
  if (!response || !response.ok) return null;
  const json = await response.json().catch(() => ({}));
  return json?.DATA || null;
}

/**
 * Reprocesa coherencia MFM para una biblioteca concreta o app completa.
 */
export async function reprocessMfmLibrary({ appCode, libraryId } = {}) {
  const response = await doFetch("MFM", "maintenance/reprocess", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ appCode, libraryId }),
  });
  if (!response || !response.ok) {
    const text = await response?.text?.().catch(() => "");
    throw new Error(text || "Error al reprocesar biblioteca MFM");
  }
  const json = await response.json().catch(() => ({}));
  return json?.DATA || null;
}

export async function getMfmProcessesSummary({ appCode, libraryId, hours } = {}) {
  const qs = new URLSearchParams();
  if (appCode) qs.set("app", appCode);
  if (libraryId != null) qs.set("libraryId", String(libraryId));
  if (hours != null) qs.set("hours", String(hours));
  const suffix = qs.toString() ? "?" + qs.toString() : "";
  const response = await doFetch("MFM", "processes/summary" + suffix, { method: "GET" });
  if (!response || !response.ok) return null;
  const json = await response.json().catch(() => ({}));
  return json?.DATA || null;
}

export async function listMfmProcesses({ appCode, libraryId, fileId, processType, status, page = 0, size = 50 } = {}) {
  const qs = new URLSearchParams();
  if (appCode) qs.set("app", appCode);
  if (libraryId != null) qs.set("libraryId", String(libraryId));
  if (fileId != null) qs.set("fileId", String(fileId));
  if (processType) qs.set("processType", processType);
  if (status) qs.set("status", status);
  qs.set("page", String(page));
  qs.set("size", String(size));

  const response = await doFetch("MFM", "processes?" + qs.toString(), { method: "GET" });
  if (!response || !response.ok) return [];
  const json = await response.json().catch(() => ({}));
  return json?.DATA || [];
}

export async function getMfmProcessDetail(idProcess) {
  if (idProcess == null) return null;
  const response = await doFetch("MFM", "processes/" + encodeURIComponent(idProcess), { method: "GET" });
  if (!response || !response.ok) return null;
  const json = await response.json().catch(() => ({}));
  return json?.DATA || null;
}

export async function getMfmProcessEvents(idProcess, { page = 0, size = 100 } = {}) {
  if (idProcess == null) return [];
  const qs = new URLSearchParams({ page: String(page), size: String(size) });
  const response = await doFetch("MFM", "processes/" + encodeURIComponent(idProcess) + "/events?" + qs.toString(), {
    method: "GET",
  });
  if (!response || !response.ok) return [];
  const json = await response.json().catch(() => ({}));
  return json?.DATA || [];
}

/**
 * Obtiene la info/metadatos de un fichero.
 */
export async function getFileInfo(uuid) {
  const response = await doFetch("MFM", uuid + "/info", { method: "GET" });
  if (!response || !response.ok) return null;
  const json = await response.json();
  return json.DATA;
}

/**
 * Obtiene el encuadre guardado para un fichero y formato concreto.
 * @param {string} uuid
 * @param {string} formatKey
 * @returns {Promise<{x:number,y:number,zoom:number,formatKey:string}|null>}
 */
export async function getFileFraming(uuid, formatKey) {
  if (!uuid || !formatKey) return null;
  const qs = new URLSearchParams({ format: formatKey }).toString();
  const response = await doFetch("MFM", uuid + "/framing?" + qs, { method: "GET" });
  if (!response || !response.ok) return null;
  const json = await response.json().catch(() => ({}));
  return json?.DATA || null;
}

export async function getAppFileFraming(appCode, uuid, formatKey, token) {
  if (!appCode || !uuid || !formatKey || !token) return null;
  const params = new URLSearchParams({ format: formatKey, token: token }).toString();
  const endpoint = "app/" + encodeURIComponent(appCode) + "/" + encodeURIComponent(uuid) + "/framing?" + params;
  const response = await doFetch("MFM", endpoint, { method: "GET" });
  if (!response || !response.ok) return null;
  const json = await response.json().catch(() => ({}));
  return json?.DATA || null;
}

export async function getPublicFileFraming(uuid, formatKey, token) {
  if (!uuid || !formatKey || !token) return null;
  const params = new URLSearchParams({ format: formatKey, token: token }).toString();
  const endpoint = "public/" + encodeURIComponent(uuid) + "/framing?" + params;
  const response = await doFetch("MFM", endpoint, { method: "GET" });
  if (!response || !response.ok) return null;
  const json = await response.json().catch(() => ({}));
  return json?.DATA || null;
}

export async function createMfmAccessLink({ uuid, appCode, ttlSeconds } = {}) {
  if (!uuid || !appCode) throw new Error("Faltan datos para generar enlace de acceso");
  const body = { uuid, appCode };
  if (ttlSeconds != null) body.ttlSeconds = ttlSeconds;

  const response = await doFetch("MFM", "access-link", {
    _silent: true,
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });

  if (!response || !response.ok) {
    const text = await response?.text?.().catch(() => "");
    throw new Error(text || "No se pudo generar enlace de acceso MFM");
  }

  const json = await response.json().catch(() => ({}));
  return json?.DATA || null;
}

/**
 * Guarda o actualiza el encuadre para un fichero/formato.
 * @param {string} uuid
 * @param {{formatKey:string,x:number,y:number,zoom:number,persist?:boolean}} payload
 */
export async function setFileFraming(uuid, payload = {}) {
  if (!uuid || !payload?.formatKey) throw new Error("Faltan datos para guardar encuadre");
  const response = await doFetch("MFM", uuid + "/framing", {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  if (!response || !response.ok) throw new Error("Error al guardar encuadre");
  const json = await response.json().catch(() => ({}));
  return json?.DATA || null;
}

/**
 * Elimina un fichero del MFM (lo envía a la papelera del usuario).
 */
export async function trashFile(uuid) {
  const response = await doFetch("MFM", uuid, { method: "DELETE" });
  if (!response || !response.ok) {
    throw new Error("Error al enviar fichero a la papelera");
  }
  return true;
}

/**
 * Lista ficheros con filtros y paginación.
 */
export async function listFiles({ app, library, mediaType, search, page = 0, size = 20 } = {}) {
  const params = new URLSearchParams();
  if (app) params.append("app", app);
  if (library) params.append("library", String(library));
  if (mediaType) params.append("mediaType", mediaType);
  if (search) params.append("search", search);
  params.append("page", String(page));
  params.append("size", String(size));

  const response = await doFetch("MFM", "list?" + params.toString(), { method: "GET" });
  if (!response || !response.ok) return [];
  const json = await response.json();
  return json.DATA;
}

/**
 * Obtiene las bibliotecas a las que el usuario tiene acceso.
 *
 * @param {string} [appCode] - Filtrar por app (opcional, ej: "TEAN")
 * @returns {Promise<Array>} Array de MfmLibraryDTO
 */
export async function getLibraries(appCode) {
  const params = appCode ? "?app=" + appCode : "";
  const response = await doFetch("MFM", "libraries" + params, { method: "GET" });
  if (!response || !response.ok) return [];
  const json = await response.json();
  return json.DATA || [];
}

/**
 * Crea una nueva biblioteca MFM.
 */
export async function createLibrary(appCode, name, description = "", parentId = null) {
  const body = { appCode, name, description };
  if (parentId != null) body.parentId = parentId;
  const response = await doFetch("MFM", "libraries", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!response || !response.ok) throw new Error("Error al crear biblioteca");
  const json = await response.json();
  return json.DATA;
}

/**
 * Actualiza nombre/descripción de una biblioteca.
 */
export async function updateLibrary(id, name, description = "") {
  const response = await doFetch("MFM", "libraries/" + id, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ name, description }),
  });
  if (!response || !response.ok) throw new Error("Error al actualizar biblioteca");
  return true;
}

/**
 * Elimina una biblioteca MFM.
 */
export async function deleteLibrary(id) {
  const response = await doFetch("MFM", "libraries/" + id, { method: "DELETE" });
  if (!response || !response.ok) throw new Error("Error al eliminar biblioteca");
  return true;
}

/**
 * Actualiza los metadatos de un fichero (título, alt text, tags).
 */
export async function updateFileMetadata(uuid, { title, altText, tags } = {}) {
  const response = await doFetch("MFM", uuid, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ title, altText, tags }),
  });
  if (!response || !response.ok) throw new Error("Error al actualizar metadatos");
  const json = await response.json();
  return json.DATA;
}

// ============================================================================
// DEDUPLICACIÓN
// ============================================================================

/**
 * Calcula el SHA-256 de un File en hex (client-side, sin subir).
 * @param {File} file
 * @returns {Promise<string|null>} hex string o null si Web Crypto no está disponible
 */
export async function computeFileSha256(file) {
  const cryptoObj = (typeof window !== "undefined" && window.crypto) || null;
  const subtle = cryptoObj?.subtle;
  if (!subtle || typeof subtle.digest !== "function") {
    // En algunos Android por HTTP (sin contexto seguro) crypto.subtle no existe.
    // Devolvemos null para permitir subida sin deduplicación en cliente.
    return null;
  }

  const buffer = await file.arrayBuffer();
  const hashBuffer = await subtle.digest("SHA-256", buffer);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map((b) => b.toString(16).padStart(2, "0")).join("");
}

/**
 * Comprueba duplicado por hash antes de subir.
 * Retorna el fichero existente si ya existe, o null si no.
 * @param {string} hash  - SHA-256 hex del contenido
 * @param {string} appCode
 * @returns {Promise<Object|null>} MfmUploadResultDTO con deduplicated=true, o null
 */
export async function checkDuplicate(hash, appCode, visibility = "private", groupIds = [], librarySlug) {
  if (!hash) return null;

  const isMfmUpload = String(appCode || "").toUpperCase() === "MFM";
  const duplicateEndpoint = isMfmUpload ? "check-duplicate-mfm" : "check-duplicate";

  const response = await doFetch("MFM", duplicateEndpoint, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ hash, appCode, visibility, groupIds, librarySlug }),
  });
  if (!response || !response.ok) return null;
  try {
    const json = await response.json();
    if (json.STATE === "SUCCESS") return json.DATA;
  } catch (e) {
    // response body was not valid JSON (e.g. empty body on 404)
  }
  return null;
}

// ============================================================================
// VISIBILIDAD
// ============================================================================

/**
 * Actualiza la visibilidad de un fichero.
 * @param {string} uuid
 * @param {string} visibility - 'private' | 'groups' | 'app' | 'public'
 * @param {number[]} [groupIds] - Obligatorio si visibility === 'groups'
 */
export async function setFileVisibility(uuid, visibility, groupIds = []) {
  const response = await doFetch("MFM", uuid + "/visibility", {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ visibility, groupIds }),
  });
  if (!response || !response.ok) throw new Error("Error al actualizar visibilidad");
  const json = await response.json();
  return json.DATA;
}

// ============================================================================
// SELECTOR (picker)
// ============================================================================

/**
 * Obtiene ficheros accesibles al usuario para el picker modal.
 * Incluye propios activos + visibilidad app/public + grupos del usuario.
 */
export async function listFilesForPicker({
  app,
  library,
  mediaType,
  search,
  page = 0,
  size = 24,
} = {}) {
  const params = new URLSearchParams();
  if (app) params.append("app", app);
  if (library != null) params.append("library", String(library));
  if (mediaType) params.append("mediaType", mediaType);
  if (search) params.append("search", search);
  params.append("page", String(page));
  params.append("size", String(size));

  const response = await doFetch("MFM", "picker?" + params.toString(), { method: "GET" });
  if (!response || !response.ok) return [];
  const json = await response.json();
  return json.DATA || [];
}

// ============================================================================
// PAPELERA
// ============================================================================

/**
 * Obtiene los ficheros en la papelera del usuario actual.
 */
export async function getTrash() {
  const response = await doFetch("MFM", "trash", { method: "GET" });
  if (!response || !response.ok) return [];
  const json = await response.json();
  return json.DATA || [];
}

/**
 * Restaura un fichero desde la papelera.
 */
export async function restoreFile(uuid) {
  const response = await doFetch("MFM", "trash/" + uuid + "/restore", { method: "POST" });
  if (!response || !response.ok) throw new Error("Error al restaurar fichero");
  return true;
}

/**
 * Elimina permanentemente un fichero de la papelera.
 */
export async function deletePermanently(uuid) {
  const response = await doFetch("MFM", "trash/" + uuid, { method: "DELETE" });
  if (!response || !response.ok) throw new Error("Error al eliminar permanentemente");
  return true;
}

// ============================================================================
// GRUPOS SSO
// ============================================================================

/**
 * Devuelve los grupos SSO asociados a una app.
 * Normaliza los campos del payload SSO ({ IDGrupo, nombre }) al formato
 * interno { idGroup, name } que usa el resto del frontend.
 */
export async function getSsoGroups(appCode) {
  console.log("[MfmService] getSsoGroups — appCode:", appCode);
  const params = appCode ? "?appCode=" + encodeURIComponent(appCode) : "";
  const fullPath = "getGruposForSharing" + params;
  console.log("[MfmService] getSsoGroups — llamando SSO/" + fullPath);
  const response = await doFetch("SSO", fullPath, { method: "GET" });
  console.log("[MfmService] getSsoGroups — response status:", response?.status, "| ok:", response?.ok);
  if (!response || !response.ok) {
    console.warn("[MfmService] getSsoGroups — respuesta NO ok, retornando []");
    return [];
  }
  const json = await response.json();
  console.log("[MfmService] getSsoGroups — json completo:", JSON.stringify(json));
  const raw = json.DATA || [];
  const normalized = raw.map((g) => ({
    idGroup: g.IDGrupo,
    name: g.nombre,
    description: g.descripcion || "",
  }));
  console.log("[MfmService] getSsoGroups — grupos normalizados:", normalized);
  return normalized;
}

/**
 * @deprecated Los grupos los gestiona SSO. Usar getSsoGroups(appCode).
 *             Se mantiene temporalmente para no romper MFMHomeView hasta su refactor.
 */
export async function getGroups(appCode) {
  const params = appCode ? "?app=" + appCode : "";
  const response = await doFetch("MFM", "groups" + params, { method: "GET" });
  if (!response || !response.ok) return [];
  const json = await response.json();
  // El endpoint MFM/groups ahora delega a SSO y devuelve { IDGrupo, nombre }
  const raw = json.DATA || [];
  return raw.map((g) => ({
    idGroup: g.IDGrupo !== undefined ? g.IDGrupo : g.idGroup,
    name: g.nombre !== undefined ? g.nombre : g.name,
  }));
}

export async function createGroup(appCode, name, description = "") {
  const response = await doFetch("MFM", "groups", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ appCode, name, description }),
  });
  if (!response || !response.ok) throw new Error("Error al crear grupo");
  const json = await response.json();
  return json.DATA;
}

export async function updateGroup(id, name, description = "") {
  const response = await doFetch("MFM", "groups/" + id, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ name, description }),
  });
  if (!response || !response.ok) throw new Error("Error al actualizar grupo");
  const json = await response.json();
  return json.DATA;
}

export async function deleteGroup(id) {
  const response = await doFetch("MFM", "groups/" + id, { method: "DELETE" });
  if (!response || !response.ok) throw new Error("Error al eliminar grupo");
  return true;
}

export async function addGroupMember(groupId, userId) {
  const response = await doFetch("MFM", "groups/" + groupId + "/members", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ userId }),
  });
  if (!response || !response.ok) throw new Error("Error al añadir miembro");
  return true;
}

export async function removeGroupMember(groupId, userId) {
  const response = await doFetch("MFM", "groups/" + groupId + "/members/" + userId, {
    method: "DELETE",
  });
  if (!response || !response.ok) throw new Error("Error al eliminar miembro");
  return true;
}

// ============================================================================
// ADMIN
// ============================================================================

/**
 * Devuelve el rol del usuario actual en MFM: { isAdmin: boolean }
 */
export async function getMfmMyRole(appCode) {
  const params = appCode ? "?app=" + encodeURIComponent(appCode) : "";
  const response = await doFetch("MFM", "my-role" + params, { method: "GET" });
  if (!response || !response.ok) return { isAdmin: false };
  const json = await response.json();
  return json.DATA || { isAdmin: false };
}

/**
 * Busca usuarios SSO (solo APP_ADMIN/APP_MANAGER).
 * Si se proporciona appCode, filtra por usuarios con permisos/grupos de esa app.
 * Retorna array de { id, apodo, nombre, apellido }.
 */
export async function searchMfmUsers(query, appCode) {
  if (!query || query.trim().length < 2) return [];
  const params = new URLSearchParams({ q: query.trim() });
  if (appCode) params.append("app", appCode);
  const response = await doFetch("MFM", "users?" + params.toString(), { method: "GET" });
  if (!response || !response.ok) return [];
  const json = await response.json();
  return json.DATA || [];
}

/**
 * Cambia el propietario de un fichero (solo APP_ADMIN).
 */
export async function changeFileOwner(uuid, newOwnerUserId) {
  const response = await doFetch("MFM", uuid + "/owner", {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ newOwnerUserId }),
  });
  if (!response || !response.ok) throw new Error("Error al cambiar propietario");
  const json = await response.json();
  return json.DATA;
}

/**
 * Devuelve la lista de propietarios de un fichero: [{ id, name }, ...] (solo APP_ADMIN).
 */
export async function getFileOwners(uuid) {
  const response = await doFetch("MFM", uuid + "/owners");
  if (!response || !response.ok) return [];
  const json = await response.json();
  return json.DATA || [];
}

/**
 * Establece la lista completa de propietarios de un fichero (solo APP_ADMIN).
 * @param {string} uuid
 * @param {number[]} ownerIds - Lista de IDs de usuario
 */
export async function setFileOwners(uuid, ownerIds) {
  const response = await doFetch("MFM", uuid + "/owners", {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ ownerIds }),
  });
  if (!response || !response.ok) throw new Error("Error al establecer propietarios");
  const json = await response.json();
  return json.DATA;
}
